import React from "react";
import { Box, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import type { useQuestionRowBaseSelection } from "@components/charts/scorecard-config/hooks/useQuestionRowBaseSelection";
import QuestionRowBaseExistingRowPicker from "./QuestionRowBaseExistingRowPicker";
import NewQuestionBaseBuilder from "./NewQuestionBaseBuilder";

type QuestionRowBaseSource = "existing_question_row" | "new_question_base";

interface QuestionRowBaseSectionProps {
  rowBaseSelection: ReturnType<typeof useQuestionRowBaseSelection>;
}

/** The "Scorecard Row Base" branch of ScorecardBaseModeSection's unified
 *  Base control: choose Existing Question Row or New Question Base, then
 *  define it. Rendered only once the researcher has selected Scorecard Row
 *  Base as the row's Base source — "No Base" lives at the outer level (as
 *  "All eligible respondents"), not here. Boxed the same way
 *  CustomBaseContainer/PreconfiguredBaseContainer box their own content, so
 *  all three Base modes read as siblings under the same radio group. */
const QuestionRowBaseSection: React.FC<QuestionRowBaseSectionProps> = ({ rowBaseSelection }) => {
  const { mode, setMode, eligibleBaseRows, existingRowId, setExistingRowId } = rowBaseSelection;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid #eee",
        borderRadius: 1,
      }}
    >
      <RadioGroup
        value={mode === "none" ? "" : mode}
        onChange={(e) => setMode(e.target.value as QuestionRowBaseSource)}
      >
        <FormControlLabel
          value="existing_question_row"
          control={<Radio size="small" />}
          label="Existing Question Row"
        />
        <FormControlLabel
          value="new_question_base"
          control={<Radio size="small" />}
          label="New Question Base"
        />
      </RadioGroup>

      {mode === "existing_question_row" && (
        <QuestionRowBaseExistingRowPicker
          eligibleRows={eligibleBaseRows}
          selectedRowId={existingRowId}
          onChange={setExistingRowId}
        />
      )}
      {mode === "new_question_base" && (
        <NewQuestionBaseBuilder rowBaseSelection={rowBaseSelection} />
      )}
    </Box>
  );
};

export default QuestionRowBaseSection;
